import { useRef, useState } from 'react';
import { formatMoney, formatQuantity, fromUnits, parseQuantity, type Centavos, type Milli } from '@abastece/shared';
import type { UnidadVenta } from '@abastece/core';
import { useT } from '../i18n/index.js';
import { etiquetaUnidad } from '../i18n/dominio.js';
import { Ico } from './Iconos.js';
import { Miniatura, MiniaturaIcono, type ProductoMiniatura } from './Miniatura.js';

const UNO = fromUnits(1);

/**
 * One line of what is being rung up, as the till draws it: the sale's lines, and an order's
 * (`ui/TomarPedido.tsx`), which the owner asked to look like the till's (2026-10-04).
 *
 * The line shows the product's name in the screen's language; a sale records `descripcion`, the
 * name it was rung up under, and that is what the receipt prints.
 *
 * On the card: what it is and what it comes to on top, the way a receipt reads, and how many at
 * what underneath. Everything else about the line (its price, its presentation, the reason for a
 * price) is in the editor, which the name opens. The name's button stretches over the whole card
 * (`after:inset-0`), so a tap anywhere on it still opens the editor, as the whole line used to; the
 * count sits above that. Square-edged, like the count's keys and the bin.
 *
 * A lesson points at the card (`data-guia`) and not at the bin beside it: pressed mid-lesson, the
 * bin would empty the sale the next steps are about to charge.
 *
 * At the till, and on an order since 2026-10-18, each line's price list is a row of its own at the foot
 * of the card (`listas`), under everything that was already there: the case of oil at «Mayorista» and
 * the rest at «Público». Above it, for a product sold in more than one presentation, a row of those
 * (`presentaciones`, the owner, 2026-10-18): the box of 12 Oreos at the box's price with one tap,
 * rather than 12 packets typed and the box's count kept in the head.
 */
export function LineaDeCarrito({
  producto,
  nombre,
  cantidad,
  onCambiarCantidad,
  subtotal,
  precio,
  precioLista,
  precioRegular,
  lleva,
  unidad,
  aviso,
  presentaciones,
  listas,
  etiquetaQuitar,
  onAbrir,
  onQuitar,
  'data-guia': guia,
}: {
  /** For its picture. None for a venta rápida, which has the quick sale's sign instead. */
  producto: ProductoMiniatura | undefined;
  nombre: string;
  cantidad: Milli;
  onCambiarCantidad: (cambio: (actual: Milli) => Milli) => void;
  subtotal: Centavos;
  /** What one costs on the line; zero while nobody has priced it. */
  precio: Centavos;
  /** The list's price: a line priced otherwise says the list's under its own. */
  precioLista: Centavos;
  /**
   * On a line in an offer (2026-11-03), the list's price without it, struck under «Oferta»; null when
   * the list has none. Undefined on any other line.
   */
  precioRegular?: Centavos | null | undefined;
  /**
   * On a line at so many for fewer (2026-11-03): its name, «2×1», how many it takes, and how many of
   * the line are free. Said whether or not any are yet, so the customer can be told.
   */
  lleva?: { nombre: string; cantidad: number; gratis: Milli } | undefined;
  unidad: UnidadVenta | null;
  /** Under the price, in red: something the line must not go out without, an expired lot. */
  aviso?: string | undefined;
  /**
   * The presentations the line's product is sold in, one tap for each, the line's own lit, over its
   * lists: the box or the packet. Only for a product with more than one, and one of the others with
   * a price on the line's list.
   */
  presentaciones?: PresentacionesDeLinea | undefined;
  /**
   * The price lists the line can go at (the owner, 2026-10-11: «choose for each item on the
   * screen»), one tap for each, the line's own lit. At the till, and on an order (2026-10-18: «like
   * in the sale screen»), only when the line has a price on more than one.
   */
  listas?: ListasDeLinea | undefined;
  /** What the bin is called to a screen reader: which line, and what it comes off. */
  etiquetaQuitar: string;
  onAbrir: () => void;
  onQuitar: () => void;
  'data-guia': string;
}) {
  const t = useT();
  return (
    <li className="entra flex items-center gap-2">
      <div
        className="relative min-w-0 flex-1 rounded-lg bg-slate-900 px-3 py-2.5 ring-1 ring-white/5 transition-colors ring-inset has-[.linea-abrir:active]:bg-slate-800 has-[.linea-abrir:hover]:bg-slate-800/60"
        data-guia={guia}
        data-varios={cantidad > UNO ? '' : undefined}
      >
        <div className="flex items-center gap-3">
          {/*
            What it is and how many down the left; what it comes to on the right, centred on the
            card the way it always sat, with what one costs under it. The count goes under the
            picture as well as the name: beside a centred amount there is not room for it next to a
            picture on a 360px phone.
          */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2.5">
              {/* The name and the bin have to fit; the picture goes first when they cannot. */}
              {producto ? (
                <Miniatura producto={producto} tamano="sm" className="rounded-md! max-[359px]:hidden" />
              ) : (
                <MiniaturaIcono icono="rapido" tamano="sm" className="rounded-md! max-[359px]:hidden" />
              )}
              <button
                type="button"
                onClick={onAbrir}
                className="linea-abrir min-w-0 flex-1 text-left font-medium after:absolute after:inset-0 after:rounded-lg focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-marca-500 focus-visible:after:ring-inset"
                data-sin-icono="fila"
              >
                {/*
                  Lines rather than one cut short. Beside the bin, a 360px phone once cut seven of the
                  starter catalogue's names, and what went was the part that tells two products apart:
                  «Acetaminofén 5…», «Gaseosa 1.5 li…». Balanced, so the break falls before «500 mg»
                  rather than inside it; a third line before anything is cut, for the longest English
                  ones.
                */}
                <span className="line-clamp-3 text-balance wrap-break-word">{nombre}</span>
              </button>
            </div>
            <div className="mt-2">
              <Contador cantidad={cantidad} producto={nombre} onCambiar={onCambiarCantidad} />
            </div>
          </div>
          <div className="tabular shrink-0 text-right">
            {/*
              The figure the line is for, big enough to read at arm's length. Keyed by the amount, so
              it pulses when + or − moves it, as the total does.
            */}
            <p key={subtotal} className="late text-xl leading-7 font-bold tracking-tight max-[359px]:text-lg">
              {formatMoney(subtotal, { symbol: false })}
            </p>
            {/* Under the amount it reads as the old line did: 3 × 15.00, per pound. */}
            <p className="mt-0.5 text-xs leading-4 text-slate-500">
              {precio > 0 ? (
                <>
                  × {formatMoney(precio, { symbol: false })}
                  {unidad && (
                    <span className="block">
                      {t('precios.porUnidad', {
                        unidad: etiquetaUnidad(unidad.nombre, unidad.nombre),
                      })}
                    </span>
                  )}
                  {precio !== precioLista && (
                    <span className="block text-amber-400">
                      {t('pos.lista')} {formatMoney(precioLista, { symbol: false })}
                    </span>
                  )}
                  {lleva && (
                    <span className="block text-teal-300" data-oferta-linea>
                      {lleva.gratis > 0
                        ? t('pos.llevaGratis', { nombre: lleva.nombre, n: formatQuantity(lleva.gratis) })
                        : t('pos.llevaFalta', { nombre: lleva.nombre, n: lleva.cantidad })}
                    </span>
                  )}
                  {precioRegular !== undefined && (
                    <span className="block text-teal-300" data-oferta-linea>
                      {t('pos.enOferta')}
                      {precioRegular !== null && (
                        <>
                          {' '}
                          <s className="text-slate-500">{formatMoney(precioRegular, { symbol: false })}</s>
                        </>
                      )}
                    </span>
                  )}
                  {aviso && <span className="block text-rose-400">{aviso}</span>}
                </>
              ) : (
                <span className="font-medium text-amber-400">{t('pos.sinPrecio')}</span>
              )}
            </p>
          </div>
        </div>
        {presentaciones && <OpcionesDeLaLinea de="presentaciones" opciones={presentaciones} etiqueta={t('pos.presentacionDeLinea', { producto: nombre })} />}
        {listas && <OpcionesDeLaLinea de="listas" opciones={listas} etiqueta={t('pos.listaDeLinea', { producto: nombre })} />}
      </div>
      {/*
        Beside the card, the way the ✕ sits beside the till's client, because a button cannot hold
        another button. Square, like the count's keys.
      */}
      <button
        type="button"
        className="flex size-11 shrink-0 items-center justify-center rounded-md border border-slate-700 bg-slate-800 transition-colors hover:bg-slate-700 active:bg-slate-700"
        aria-label={etiquetaQuitar}
        onClick={onQuitar}
      >
        <Ico icono="quitar" className="size-4 text-rose-400" />
      </button>
    </li>
  );
}

export interface ListasDeLinea {
  /** In the order «Cobrar a» shows them. Without a price for the line's presentation, it cannot be picked. */
  opciones: readonly { id: string; nombre: string; conPrecio: boolean }[];
  /** The list the line's price came from. */
  elegida: string;
  onElegir: (listaId: string) => void;
}

/**
 * The presentations of the line's product, smallest first, as «Unidad» and «Paquete (12)» read
 * (`etiquetaPresentacion`). Without a price on the line's list, one cannot be picked.
 */
export interface PresentacionesDeLinea {
  opciones: readonly { id: string; nombre: string; conPrecio: boolean }[];
  /** The presentation the line sells. */
  elegida: string;
  onElegir: (unidadVentaId: string) => void;
}

/**
 * A line's choices, side by side across the foot of its card, as the drawer's C$ and US$ are: its
 * price lists, and over them the presentations it is sold in, the line's own lit the way «Cobrar a»
 * lights the sale's. Above the name's button, which stretches over the whole card, so a tap here
 * picks and does not open the editor.
 */
function OpcionesDeLaLinea({
  de,
  opciones: grupo,
  etiqueta,
}: {
  de: 'listas' | 'presentaciones';
  opciones: ListasDeLinea | PresentacionesDeLinea;
  etiqueta: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={etiqueta}
      className="relative z-10 mt-2 flex gap-1 rounded-md bg-slate-800 p-0.5"
      data-linea-listas={de === 'listas' ? '' : undefined}
      data-linea-presentaciones={de === 'presentaciones' ? '' : undefined}
    >
      {grupo.opciones.map((o) => {
        const elegida = o.id === grupo.elegida;
        return (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={elegida}
            disabled={!elegida && !o.conPrecio}
            onClick={() => {
              if (!elegida) grupo.onElegir(o.id);
            }}
            data-sin-icono="pestana"
            data-lista={de === 'listas' ? o.id : undefined}
            data-presentacion={de === 'presentaciones' ? o.id : undefined}
            className={`min-h-9 min-w-0 flex-1 truncate rounded px-2 text-sm font-medium transition-colors disabled:opacity-40 ${
              elegida ? 'bg-teal-500/15 text-teal-100 ring-1 ring-teal-500/60 ring-inset' : 'text-slate-400 enabled:hover:bg-white/5 enabled:hover:text-slate-200 active:bg-slate-700'
            }`}
          >
            {o.nombre}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Under the camera's card, the line a scan just rang up: how many at what, and which presentation
 * when the product has more than one (`OpcionesEscaner.describir`). The till's and an order's.
 */
export function LineaEscaneada({
  cantidad,
  precio,
  unidad,
}: {
  cantidad: Milli;
  precio: Centavos;
  /** Only when the product has several: the case, not the can. */
  unidad: UnidadVenta | null;
}) {
  const t = useT();
  return (
    <p className="tabular">
      {formatQuantity(cantidad)} ×{' '}
      {precio > 0 ? formatMoney(precio) : <span className="font-medium text-amber-300">{t('pos.sinPrecio')}</span>}
      {unidad && <> {t('precios.porUnidad', { unidad: etiquetaUnidad(unidad.nombre, unidad.nombre) })}</>}
    </p>
  );
}

/**
 * The most a line's number will take by typing.
 *
 * A USB scanner types its code and presses Enter wherever the cursor is. Scanned while the cursor
 * was still in a line's number, a can of soda would ring up as 7,501,055,300,846 cans. Nobody
 * types ten thousand of one thing at a counter; the line's editor still takes any amount.
 */
const TOPE_ESCRITO = fromUnits(9_999);

/**
 * A line's count: − and + for one fewer and one more, and the number between them to type
 * twelve without pressing + eleven times.
 *
 * It never reaches nothing. − stops at one and a typed 0 is refused: taking a line off is the
 * bin's job, beside the line, and the bin asks. Less than one by typing is still a sale, half a
 * pound of cheese, so − only stops going further down. Typing starts from an empty box with the
 * current count showing faintly, so what is typed replaces the number instead of landing beside
 * it; leaving the box empty, or pressing Escape, keeps it.
 *
 * «Hacer una venta» points at it at the till (`pos-cantidad`); on an order it is the same count,
 * and no lesson looks for it there.
 */
function Contador({
  cantidad,
  producto,
  onCambiar,
}: {
  cantidad: Milli;
  /** Whose count this is, for what a screen reader says. */
  producto: string;
  onCambiar: (cambio: (actual: Milli) => Milli) => void;
}) {
  const t = useT();
  const grupo = useRef<HTMLDivElement>(null);
  /** What is being typed into the number; `null` when nobody is typing. */
  const [escrito, setEscrito] = useState<string | null>(null);
  const descartar = useRef(false);
  const actual = formatQuantity(cantidad);
  /**
   * As wide as what it holds, so 0.125 and 1500 are not cut, up to six figures; past that it
   * scrolls rather than squeezing the name. `ch` is a digit's width, and these are tabular.
   */
  const cifras = Math.min(Math.max((escrito ?? '').length, actual.length), 6);

  /** A number it will not take: shaken, the way a wrong PIN is, and left as it was. */
  function sacudir() {
    const el = grupo.current;
    if (!el) return;
    el.classList.remove('sacude');
    void el.offsetWidth;
    el.classList.add('sacude');
  }

  function fijar(texto: string) {
    setEscrito(null);
    if (descartar.current) {
      descartar.current = false;
      return;
    }
    if (!texto.trim()) return;
    const nueva = parseQuantity(texto);
    if (nueva === null || nueva <= 0 || nueva > TOPE_ESCRITO) return sacudir();
    onCambiar(() => nueva);
  }

  return (
    <div
      ref={grupo}
      role="group"
      aria-label={t('pos.cantidadDe', { producto })}
      className="relative z-10 flex h-10 w-fit shrink-0 divide-x divide-slate-700 overflow-hidden rounded-md border border-slate-700 bg-slate-800"
      data-guia="pos-cantidad"
    >
      <button
        type="button"
        className="flex w-9 items-center justify-center transition-colors enabled:hover:bg-slate-700 active:bg-slate-700 disabled:opacity-30 max-[359px]:w-8"
        aria-label={t('pos.unoMenos')}
        disabled={cantidad <= UNO}
        onClick={() => onCambiar((q) => Math.max(q - UNO, UNO) as Milli)}
      >
        <Ico icono="restar" className="size-4" />
      </button>
      <input
        className="tabular min-w-11 bg-transparent px-1.5 max-[359px]:min-w-10 text-center text-base font-semibold placeholder:text-slate-500 focus:bg-slate-900 focus:ring-2 focus:ring-marca-500 focus:outline-none focus:ring-inset"
        style={{ width: `calc(${cifras}ch + 0.75rem)` }}
        inputMode="decimal"
        enterKeyHint="done"
        autoComplete="off"
        aria-label={t('pos.cantidadDe', { producto })}
        value={escrito ?? actual}
        placeholder={actual}
        onFocus={() => setEscrito('')}
        onChange={(e) => setEscrito(e.target.value)}
        onBlur={(e) => fijar(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') {
            descartar.current = true;
            e.currentTarget.blur();
          }
        }}
      />
      <button
        type="button"
        className="flex w-9 items-center justify-center transition-colors hover:bg-slate-700 active:bg-slate-700 max-[359px]:w-8"
        aria-label={t('pos.unoMas')}
        onClick={() => onCambiar((q) => (q + UNO) as Milli)}
      >
        <Ico icono="sumar" className="size-4" />
      </button>
    </div>
  );
}
